import type {HalImage, HalImagePhysical, HalImageView, HalImageVirtual} from "#hal/hal-image/index"
import type {HalPainterImageCompositor} from "#hal/hal-painter-image-compositor/index"
import type {HalEntity} from "#hal/hal-context"
import type {HalPainterPrimitive} from "#hal/hal-painter-primitive/index"
import {RefCountableBase} from "@cm/utils"
import {isHalImagePhysical, isHalImageView, isHalImageVirtual} from "#hal/hal-image/utils"
import type {HalPainterImageBlit} from "#hal/hal-painter-image-blit/index"

export class HalRef<T extends HalEntity> extends RefCountableBase {
    constructor(ref: T, disposeFn?: (ref: T) => void) {
        super({
            disposeFn: disposeFn ? () => disposeFn(ref) : undefined,
        })
        this._ref = ref
    }

    get ref(): T {
        if (this.refCount <= 0) {
            throw new Error("Ref is disposed")
        }
        return this._ref
    }

    private _ref: T
}

export type HalImageVirtualRef = HalRef<HalImageVirtual>
export type HalImagePhysicalRef = HalRef<HalImagePhysical>
export type HalImageViewRef = HalRef<HalImageView>
export type HalImageRef = HalRef<HalImage>

export type HalPainterPrimitiveRef = HalRef<HalPainterPrimitive>
export type HalPainterImageCompositorRef = HalRef<HalPainterImageCompositor>
export type HalPainterImageBlitRef = HalRef<HalPainterImageBlit>

export const isHalRef = (data: unknown): data is HalRef<HalEntity> => {
    return (
        data != null &&
        typeof data === "object" &&
        "refCount" in data &&
        typeof (data as HalRef<HalEntity>).refCount === "number" &&
        "ref" in data &&
        typeof (data as HalRef<HalEntity>).ref === "object"
    )
}

export const isHalImageVirtualRef = (data: unknown): data is HalImageVirtualRef => {
    return isHalRef(data) && isHalImageVirtual(data.ref)
}

export const isHalImagePhysicalRef = (data: unknown): data is HalImagePhysicalRef => {
    return isHalRef(data) && isHalImagePhysical(data.ref)
}

export const isHalImageViewRef = (data: unknown): data is HalImageViewRef => {
    return isHalRef(data) && isHalImageView(data.ref)
}
